import {memo, useCallback, useMemo} from 'react';
import type {ChangeEvent, KeyboardEventHandler} from 'react';

import {debounce} from '#utils/debounce.js';

import {Search as SearchComponent} from '../components/search.js';
import {useMultiComboBoxContext} from '../context.js';

type SearchProps = {
  placeholder?: string;
  isHidden?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
};
const Search = memo(function Search({
  placeholder,
  isHidden,
  onKeyDown,
}: SearchProps) {
  const {
    as,
    getInputProps,
    handleOnSearchKeyDown,
    setInputValue,
    loadItems,
    setItemsList,
    setHasMore,
    hasExternalInfiniteLoader,
  } = useMultiComboBoxContext();
  const debouncedHandleOnChange = useMemo(
    () =>
      debounce(async (value: string) => {
        if (loadItems) {
          const {items, hasMore} = await loadItems(value);
          setItemsList(items);
          setHasMore(hasMore);
        }
      }, 300),
    [loadItems, setItemsList, setHasMore]
  );
  const handleOnChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (evt.target instanceof HTMLInputElement) {
        setInputValue(evt.target.value);
        if (loadItems) {
          setHasMore(false);
          debouncedHandleOnChange(evt.target.value);
        } else if (hasExternalInfiniteLoader) {
          setHasMore(true);
        }
      }
    },
    [
      debouncedHandleOnChange,
      hasExternalInfiniteLoader,
      loadItems,
      setHasMore,
      setInputValue,
    ]
  );
  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    event => {
      onKeyDown?.(event);

      if (!event.defaultPrevented) {
        handleOnSearchKeyDown(event);
      }
    },
    [handleOnSearchKeyDown, onKeyDown]
  );

  return (
    <SearchComponent
      isHidden={isHidden ?? as === 'MultiSelect'}
      placeholder={placeholder}
      {...getInputProps({
        onKeyDown: handleKeyDown,
        onChange: handleOnChange,
      })}
    />
  );
});

export {Search};
